import { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { BarChart3, PieChart as PieChartIcon, TrendingUp, Settings2 } from 'lucide-react';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'];

// Parâmetros disponíveis para análise SUS
const AXIS_OPTIONS = {
  x: [
    { value: 'faixa_etaria', label: 'Faixa Etária', type: 'category' },
    { value: 'unidade_saude', label: 'Unidade de Saúde', type: 'category' },
    { value: 'medicamento', label: 'Medicamento', type: 'category' },
    { value: 'mes_ano', label: 'Mês/Ano', type: 'time' },
    { value: 'tipo_uso', label: 'Tipo de Uso (Oral/Tópico/etc)', type: 'category' },
    { value: 'status_receita', label: 'Status da Receita', type: 'category' },
  ],
  y: [
    { value: 'quantidade_pacientes', label: 'Quantidade de Pacientes', type: 'number' },
    { value: 'quantidade_receitas', label: 'Quantidade de Receitas', type: 'number' },
    { value: 'quantidade_medicamentos', label: 'Quantidade de Medicamentos', type: 'number' },
    { value: 'media_idade', label: 'Média de Idade', type: 'number' },
    { value: 'receitas_vencendo', label: 'Receitas a Vencer', type: 'number' },
    { value: 'entregas_realizadas', label: 'Entregas Realizadas', type: 'number' },
  ]
};

const CHART_TYPES = [
  { value: 'bar', label: 'Barras', icon: BarChart3 },
  { value: 'pie', label: 'Pizza', icon: PieChartIcon },
  { value: 'line', label: 'Linha', icon: TrendingUp },
];

export default function InteractiveChart({ 
  patients = [], 
  prescriptions = [], 
  healthUnits = [],
  deliveryDates = [],
  title = "Análise Personalizada",
  description = "Configure os eixos para visualizar diferentes métricas"
}) {
  const [xAxis, setXAxis] = useState('faixa_etaria');
  const [yAxis, setYAxis] = useState('quantidade_pacientes');
  const [chartType, setChartType] = useState('bar');

  // Função para calcular idade
  const calculateAge = (birthDateStr) => {
    if (!birthDateStr || birthDateStr === 'N/A' || birthDateStr === '01/01/1970') return null;
    try {
      let birthDate;
      if (birthDateStr.includes('/')) {
        const parts = birthDateStr.split('/');
        if (parts.length === 3) {
          birthDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      } else {
        birthDate = new Date(birthDateStr);
      }
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age >= 0 && age < 150 ? age : null;
    } catch {
      return null;
    }
  };

  // Função para obter faixa etária
  const getAgeRange = (age) => {
    if (age === null) return 'Não informado';
    if (age <= 12) return '0-12 anos';
    if (age <= 18) return '13-18 anos';
    if (age <= 30) return '19-30 anos';
    if (age <= 45) return '31-45 anos';
    if (age <= 60) return '46-60 anos';
    if (age <= 75) return '61-75 anos';
    return '76+ anos';
  };

  // Criar lookup de unidades
  const unitLookup = useMemo(() => {
    const lookup = {};
    healthUnits.forEach(u => { lookup[u.id] = u.nome; });
    return lookup;
  }, [healthUnits]);

  // Criar lookup de pacientes
  const patientLookup = useMemo(() => {
    const lookup = {};
    patients.forEach(p => { lookup[p.id] = p; });
    return lookup;
  }, [patients]);

  // Gerar dados baseado nos eixos selecionados
  const chartData = useMemo(() => {
    const groupedData = {};

    // Função para obter valor do eixo X
    const getXValue = (patient, prescription) => {
      switch (xAxis) {
        case 'faixa_etaria':
          const age = calculateAge(patient?.data_nascimento);
          return getAgeRange(age);
        case 'unidade_saude':
          return unitLookup[patient?.unit_id] || 'Sem unidade';
        case 'medicamento':
          if (prescription?.medicamentos?.[0]) {
            return prescription.medicamentos[0].name?.substring(0, 20) || 'N/A';
          }
          return 'N/A';
        case 'mes_ano':
          if (prescription?.data_criacao) {
            const date = new Date(prescription.data_criacao);
            return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
          }
          return 'N/A';
        case 'tipo_uso':
          return prescription?.uso || 'N/A';
        case 'status_receita':
          if (prescription?.data_validade) {
            const expiry = new Date(prescription.data_validade);
            const today = new Date();
            const daysUntil = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
            if (daysUntil < 0) return 'Vencida';
            if (daysUntil <= 30) return 'Vencendo (30d)';
            return 'Válida';
          }
          return 'N/A';
        default:
          return 'N/A';
      }
    };

    // Processar dados baseado no eixo Y
    if (yAxis === 'quantidade_pacientes' || yAxis === 'media_idade') {
      patients.forEach(patient => {
        const xValue = getXValue(patient, null);
        if (!groupedData[xValue]) {
          groupedData[xValue] = { count: 0, ages: [] };
        }
        groupedData[xValue].count++;
        const age = calculateAge(patient.data_nascimento);
        if (age !== null) groupedData[xValue].ages.push(age);
      });
    } else if (yAxis === 'quantidade_receitas' || yAxis === 'quantidade_medicamentos' || yAxis === 'receitas_vencendo') {
      prescriptions.forEach(presc => {
        const patient = patientLookup[presc.patient_id];
        const xValue = getXValue(patient, presc);
        if (!groupedData[xValue]) {
          groupedData[xValue] = { count: 0, medCount: 0, expiring: 0 };
        }
        groupedData[xValue].count++;
        groupedData[xValue].medCount += presc.medicamentos?.length || 0;
        
        // Check if expiring
        if (presc.data_validade) {
          const expiry = new Date(presc.data_validade);
          const today = new Date();
          const daysUntil = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
          if (daysUntil <= 30) groupedData[xValue].expiring++;
        }
      });
    } else if (yAxis === 'entregas_realizadas') {
      deliveryDates.forEach(delivery => {
        const presc = prescriptions.find(p => p.id === delivery.prescription_id);
        const patient = presc ? patientLookup[presc.patient_id] : null;
        const xValue = getXValue(patient, presc);
        if (!groupedData[xValue]) {
          groupedData[xValue] = { count: 0 };
        }
        groupedData[xValue].count++;
      });
    }

    // Converter para array e calcular valor Y
    const result = Object.entries(groupedData).map(([name, data]) => {
      let value = 0;
      switch (yAxis) {
        case 'quantidade_pacientes':
        case 'quantidade_receitas':
        case 'entregas_realizadas':
          value = data.count;
          break;
        case 'quantidade_medicamentos':
          value = data.medCount || 0;
          break;
        case 'media_idade':
          value = data.ages?.length > 0 
            ? Math.round(data.ages.reduce((a, b) => a + b, 0) / data.ages.length)
            : 0;
          break;
        case 'receitas_vencendo':
          value = data.expiring || 0;
          break;
        default:
          value = data.count;
      }
      return { name, value };
    });

    // Ordenar
    return result.sort((a, b) => b.value - a.value).slice(0, 15);
  }, [xAxis, yAxis, patients, prescriptions, healthUnits, deliveryDates, unitLookup, patientLookup]);

  const yLabel = AXIS_OPTIONS.y.find(o => o.value === yAxis)?.label || yAxis;
  const xLabel = AXIS_OPTIONS.x.find(o => o.value === xAxis)?.label || xAxis;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">
            {chartData.length} categorias
          </Badge>
        </div>
        
        {/* Controles */}
        <div className="flex flex-wrap gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
          {/* Tipo de Gráfico */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Tipo de Gráfico</label>
            <Select value={chartType} onValueChange={setChartType}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHART_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Eixo X */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Eixo X (Categorias)</label>
            <Select value={xAxis} onValueChange={setXAxis}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AXIS_OPTIONS.x.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Eixo Y */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Eixo Y (Valores)</label>
            <Select value={yAxis} onValueChange={setYAxis}>
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AXIS_OPTIONS.y.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {chartData.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum dado disponível para esta combinação</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            {chartType === 'bar' ? (
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  fontSize={11}
                  tick={{ fill: '#666' }}
                />
                <YAxis 
                  tick={{ fill: '#666' }}
                  label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: '#666', fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value) => [value, yLabel]}
                  labelFormatter={(label) => `${xLabel}: ${label}`}
                />
                <Legend />
                <Bar dataKey="value" name={yLabel} radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            ) : chartType === 'pie' ? (
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={130}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, yLabel]} />
                <Legend />
              </PieChart>
            ) : (
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  fontSize={11}
                />
                <YAxis 
                  label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: '#666', fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value) => [value, yLabel]}
                  labelFormatter={(label) => `${xLabel}: ${label}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  name={yLabel}
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ fill: '#10B981', strokeWidth: 2 }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
